/**
 * Cartoon art for the evolution track behind the HUD ring (horizontal layout) and strip
 * (vertical layout), as inline SVG markup.
 *
 * The track is a chunky inked tube: flat colour bands that step from sea cyan to sunny gold
 * along the chain, cel shaded the Ghost Circuit way (a lit crescent on the edges facing the
 * upper left, a shade crescent on the edges facing the lower right), one ink outline round
 * the whole silhouette, and a fat gold arrowhead at the end of the chain. Colour steps sit
 * under the chip centres, so every visible gap between two chips reads as one flat colour.
 *
 * Markup only: the ring and strip builders (render/evolution-ring-dom.js,
 * render/evolution-strip-dom.js) own the DOM contracts. Nothing here touches layout maths;
 * the ring geometry mirrors mockup-evolution-ring.css (chip orbit 40cqi, SVG at 98.5%).
 */
import { INK, mixHex } from "../palette.js";

/** Steps of the track colour along the chain (cyan sea → green → sunny gold). */
export var TRACK_HUES = ["#45c9f6", "#2cc6c6", "#45d482", "#a6d63c", "#ffc533"];
/** The arrowhead: a warmer gold so the end of the chain pops. */
export var TRACK_ARROW = { base: "#ffaa22", light: "#ffe27c", shade: "#e27a12" };

var CX = 256;
var CY = 256;
/** Chip orbit in the ring's 512 viewBox: 40cqi of the ring box over the 98.5% SVG box. */
export var RING_TRACK_RC = (0.4 / 0.985) * 512;
/** Tube thickness; chips (~104 units) overhang it a little, like beads on a band. */
var RING_T = 74;
var RING_RO = RING_TRACK_RC + RING_T / 2;
var RING_RI = RING_TRACK_RC - RING_T / 2;
/** The tube starts just before chip 0 (12 o'clock) under its chip, with a round cap. */
var RING_START_DEG = -6;
/** Arrowhead over the empty tail slot (chips never reach past ~295°, even squeezed). */
var RING_ARROW_BASE_DEG = 313;
var RING_ARROW_TIP_DEG = 343;
var RING_ARROW_FLARE = 13;
/** Ink weight in viewBox units (~2.5 CSS px on a ~130 px ring): stroked at twice, under the fills. */
var RING_INK = 10;
/** Cel crescent offsets (viewBox units): the lit body shifts up-left, the light patch down-right. */
var RING_SHADE_SHIFT = [-8, -11];
var RING_LIGHT_SHIFT = [8, 10];
/** Sunken centre dish behind the hub creature. */
var RING_DISH_R = RING_RI - 18;

var svgSeq = 0;

function fmt(n) {
  return String(Math.round(n * 100) / 100);
}

/** Point on the ring at radius r, `deg` clockwise from 12 o'clock. */
function pt(r, deg, dx, dy) {
  var a = (deg * Math.PI) / 180;
  return fmt(CX + r * Math.sin(a) + (dx || 0)) + " " + fmt(CY - r * Math.cos(a) + (dy || 0));
}

/**
 * Colour on the cyan → gold ramp.
 * @param {number} t 0–1
 */
export function trackHue(t) {
  var k = Math.max(0, Math.min(1, t)) * (TRACK_HUES.length - 1);
  var i = Math.min(TRACK_HUES.length - 2, Math.floor(k));
  return mixHex(TRACK_HUES[i], TRACK_HUES[i + 1], k - i);
}

/** Whole-track silhouette (tube + start cap + arrowhead), shifted by (dx, dy). */
function ringSilhouette(dx, dy) {
  var large = RING_ARROW_BASE_DEG - RING_START_DEG > 180 ? 1 : 0;
  var capR = fmt(RING_T / 2);
  return "M" + pt(RING_RO, RING_START_DEG, dx, dy)
    + "A" + fmt(RING_RO) + " " + fmt(RING_RO) + " 0 " + large + " 1 " + pt(RING_RO, RING_ARROW_BASE_DEG, dx, dy)
    + "L" + pt(RING_RO + RING_ARROW_FLARE, RING_ARROW_BASE_DEG, dx, dy)
    + "L" + pt(RING_TRACK_RC, RING_ARROW_TIP_DEG, dx, dy)
    + "L" + pt(RING_RI - RING_ARROW_FLARE, RING_ARROW_BASE_DEG, dx, dy)
    + "L" + pt(RING_RI, RING_ARROW_BASE_DEG, dx, dy)
    + "A" + fmt(RING_RI) + " " + fmt(RING_RI) + " 0 " + large + " 0 " + pt(RING_RI, RING_START_DEG, dx, dy)
    + "A" + capR + " " + capR + " 0 0 1 " + pt(RING_RO, RING_START_DEG, dx, dy)
    + "Z";
}

/** Annular sector of the tube between two angles (clockwise). */
function ringSector(fromDeg, toDeg, withCap) {
  var large = toDeg - fromDeg > 180 ? 1 : 0;
  var capR = fmt(RING_T / 2);
  return "M" + pt(RING_RO, fromDeg)
    + "A" + fmt(RING_RO) + " " + fmt(RING_RO) + " 0 " + large + " 1 " + pt(RING_RO, toDeg)
    + "L" + pt(RING_RI, toDeg)
    + "A" + fmt(RING_RI) + " " + fmt(RING_RI) + " 0 " + large + " 0 " + pt(RING_RI, fromDeg)
    + (withCap ? "A" + capR + " " + capR + " 0 0 1 " + pt(RING_RO, fromDeg) : "")
    + "Z";
}

/** Everything outside `inner` (evenodd), so a clip to the silhouette leaves a crescent. */
function crescent(innerPath) {
  return "M-64 -64H576V576H-64Z" + innerPath;
}

/**
 * Inner SVG markup for the ring track (viewBox 0 0 512 512).
 *
 * @param {number} chipCount chips on the arc
 * @param {number} chipStepDeg degrees between chip centres
 * @returns {string}
 */
export function buildRingTrackMarkup(chipCount, chipStepDeg) {
  svgSeq += 1;
  var clipId = "evoTrackClip-" + svgSeq;
  var dishClipId = "evoDishClip-" + svgSeq;
  var n = Math.max(1, Math.floor(Number(chipCount) || 1));
  var step = Number(chipStepDeg) || 360 / 11;
  // Colour steps at chip centres: [start, chip 1, chip 2, …, chip n−1, arrow base].
  var bounds = [RING_START_DEG];
  for (var i = 1; i < n; i++) {
    var a = i * step;
    if (a < RING_ARROW_BASE_DEG - 2) bounds.push(a);
  }
  bounds.push(RING_ARROW_BASE_DEG);
  var segs = bounds.length - 1;
  var sil = ringSilhouette(0, 0);

  var out = "<defs>"
    + "<clipPath id=\"" + clipId + "\"><path d=\"" + sil + "\"/></clipPath>"
    + "<clipPath id=\"" + dishClipId + "\"><circle cx=\"256\" cy=\"256\" r=\"" + fmt(RING_DISH_R) + "\"/></clipPath>"
    + "</defs>";

  // Sunken dish for the hub: shade crescent on its upper-left inner edge (light from the
  // upper left falls into the hollow), a thin soft-ink rim.
  out += "<g class=\"evo-ring__dish\">"
    + "<circle class=\"evo-ring__dish-fill\" cx=\"256\" cy=\"256\" r=\"" + fmt(RING_DISH_R) + "\"/>"
    + "<path class=\"evo-ring__dish-shade\" clip-path=\"url(#" + dishClipId + ")\" fill-rule=\"evenodd\" d=\""
    + crescent("M" + fmt(256 + 9) + " " + fmt(256 + 12) + "m-" + fmt(RING_DISH_R) + " 0a" + fmt(RING_DISH_R) + " " + fmt(RING_DISH_R) + " 0 1 0 " + fmt(RING_DISH_R * 2) + " 0a" + fmt(RING_DISH_R) + " " + fmt(RING_DISH_R) + " 0 1 0 -" + fmt(RING_DISH_R * 2) + " 0Z")
    + "\"/>"
    + "<circle class=\"evo-ring__dish-rim\" cx=\"256\" cy=\"256\" r=\"" + fmt(RING_DISH_R) + "\" fill=\"none\"/>"
    + "</g>";

  // Ink: the silhouette filled and stroked at twice the weight, painted under the fills so
  // only the outer half shows.
  out += "<path class=\"evo-ring__track-ink\" d=\"" + sil + "\" fill=\"" + INK + "\" stroke=\"" + INK
    + "\" stroke-width=\"" + RING_INK * 2 + "\" stroke-linejoin=\"round\"/>";

  out += "<g class=\"evo-ring__track\">";
  for (var s = 0; s < segs; s++) {
    var from = bounds[s];
    // Overlap the next band a little so anti-aliased seams never show the ink beneath.
    var to = Math.min(bounds[s + 1] + 0.8, RING_ARROW_BASE_DEG + 1);
    var hue = trackHue(segs > 1 ? s / (segs - 1) : 1);
    out += "<path d=\"" + ringSector(from, to, s === 0) + "\" fill=\"" + hue + "\"/>";
  }
  out += "<path d=\"M" + pt(RING_RO + RING_ARROW_FLARE, RING_ARROW_BASE_DEG)
    + "L" + pt(RING_TRACK_RC, RING_ARROW_TIP_DEG)
    + "L" + pt(RING_RI - RING_ARROW_FLARE, RING_ARROW_BASE_DEG) + "Z\" fill=\"" + TRACK_ARROW.base + "\"/>";
  out += "</g>";

  // Cel crescents, clipped to the silhouette: shade lower right, light upper left.
  out += "<g clip-path=\"url(#" + clipId + ")\">"
    + "<path class=\"evo-ring__track-shade\" fill-rule=\"evenodd\" d=\"" + crescent(ringSilhouette(RING_SHADE_SHIFT[0], RING_SHADE_SHIFT[1])) + "\"/>"
    + "<path class=\"evo-ring__track-light\" fill-rule=\"evenodd\" d=\"" + crescent(ringSilhouette(RING_LIGHT_SHIFT[0], RING_LIGHT_SHIFT[1])) + "\"/>"
    + "</g>";

  // Glints: a capsule on the arrowhead and a dot beside it, upper left like every glint.
  var g1 = pt(RING_TRACK_RC + 14, RING_ARROW_BASE_DEG + 7).split(" ");
  var g2 = pt(RING_TRACK_RC + 30, RING_ARROW_BASE_DEG + 4).split(" ");
  out += "<g class=\"evo-ring__track-glint\" fill=\"#ffffff\">"
    + "<ellipse cx=\"" + g1[0] + "\" cy=\"" + g1[1] + "\" rx=\"13\" ry=\"6\" transform=\"rotate(-38 " + g1[0] + " " + g1[1] + ")\"/>"
    + "<circle cx=\"" + g2[0] + "\" cy=\"" + g2[1] + "\" r=\"4.5\"/>"
    + "</g>";
  return out;
}

/**
 * Gradient stops for the strip tube: hard colour steps at the chip centres.
 * The strip SVG spans chip 0's centre to the last chip's centre (see
 * mockup-evolution-strip.css), so chip k sits at k / (count − 1).
 *
 * @param {number} chipCount
 * @returns {string} `<stop>` markup
 */
export function buildStripTrackStops(chipCount) {
  var n = Math.max(2, Math.floor(Number(chipCount) || 2));
  var gaps = n - 1;
  var out = "";
  for (var k = 0; k < gaps; k++) {
    var hue = trackHue(gaps > 1 ? k / (gaps - 1) : 1);
    out += "<stop offset=\"" + fmt(k / gaps) + "\" stop-color=\"" + hue + "\"/>";
    out += "<stop offset=\"" + fmt((k + 1) / gaps) + "\" stop-color=\"" + hue + "\"/>";
  }
  return out;
}

/**
 * Overlays drawn over the strip tube (viewBox 0 0 1000 100, stretched): a lit band along
 * the top, a shade band along the bottom.
 * @returns {string}
 */
export function buildStripTrackBands() {
  return "<rect class=\"evo-strip__band-light\" x=\"0\" y=\"0\" width=\"1000\" height=\"26\"/>"
    + "<rect class=\"evo-strip__band-shade\" x=\"0\" y=\"74\" width=\"1000\" height=\"26\"/>";
}

/**
 * The strip's arrowhead (its own uniformly scaled SVG, viewBox 0 0 40 48): it pokes out
 * from under the last chip, pointing along the chain.
 * @returns {string}
 */
export function buildStripArrowMarkup() {
  return "<path class=\"evo-strip__arrow-ink\" d=\"M2 4 L36 24 L2 44 Z\" fill=\"" + INK + "\" stroke=\"" + INK
    + "\" stroke-width=\"7\" stroke-linejoin=\"round\"/>"
    + "<path d=\"M2 4 L36 24 L2 44 Z\" fill=\"" + TRACK_ARROW.shade + "\"/>"
    + "<path d=\"M2 4 L33 22.2 L2 38 Z\" fill=\"" + TRACK_ARROW.base + "\"/>"
    + "<path d=\"M2 4 L22 15.8 L2 18 Z\" fill=\"" + TRACK_ARROW.light + "\"/>"
    + "<ellipse cx=\"10\" cy=\"14\" rx=\"4.2\" ry=\"2.2\" transform=\"rotate(28 10 14)\" fill=\"#ffffff\"/>";
}
